import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  input,
  output,
  inject,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  AutomationEventType,
  CommunicationDirection,
  OwnershipLevel,
} from '@fitness-force/enum';
import type {
  ClientById,
  ClientInteraction,
  NotificationError,
  SendFreeFormSmsRequest,
  SendSmsTemplateRequest,
  SMSTemplateMaster,
  SmsTemplateMaster,
  WhatsAppTemplateMaster,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@fitness-force/api';
import { ToastMessageService } from '@fitness-force/api';
import type { WhatsAppSenderConfigurationMasterResponse } from '@fitness-force/models';
import type { EmailFromMaster } from '@fitness-force/models';
import type {
  EmailTemplateMaster,
  EmailTemplateMasterById,
  SendEmailTemplateRequest,
  SendFreeFormEmailRequest,
} from '@fitness-force/models';
import type { SMSFromMaster } from '@fitness-force/models';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, forkJoin, map, Subject, takeUntil } from 'rxjs';
import { AuthSevices, TimeconversionService } from '@fitness-force/api';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationMasterStateService } from '../../services/notificationmasterstateservice';
import { CORPORATE_CONFIG_DETAILS } from '@fitness-force/constants';
@Component({
  selector: 'reply',
  standalone: true,
  imports: [
    EditorModule,
    TranslateModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    TooltipModule,
    AsyncPipe,
    NgClass,
  ],
  templateUrl: './reply.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  styles: [
    `
      .hidden {
        display: none !important;
      }
    `,
  ],
})
export class ReplyComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private fb = inject(FormBuilder);
  toastMessageService = inject(ToastMessageService);
  authservice = inject(AuthSevices);
  timeService = inject(TimeconversionService);
  private notificationMasterStateService = inject(
    NotificationMasterStateService,
  );

  readonly client = input<ClientById>({} as ClientById);
  readonly tenantId = input(0);
  readonly lastInteraction = input<ClientInteraction>({} as ClientInteraction);
  readonly showDotColor = input<string>('text-red-500');
  readonly multiChannel = input<boolean>(true);
  readonly replyFrom = input<string>('Email');
  readonly newInteraction = output<ClientInteraction>();
  readonly editor = viewChild<any>('editor');
  messageReplyForm: UntypedFormGroup = new UntypedFormGroup({});
  RepliesFrom = new BehaviorSubject<any[]>([] as any[]);

  emailTemplateMasters$ = new BehaviorSubject<EmailTemplateMaster[]>(
    [] as EmailTemplateMaster[],
  );
  emailTemplateMaster$ = new BehaviorSubject<EmailTemplateMasterById | null>(
    {} as EmailTemplateMasterById,
  );
  smsTemplateMasters$ = new BehaviorSubject<SmsTemplateMaster[]>(
    [] as SmsTemplateMaster[],
  );
  smsTemplateMaster$ = new BehaviorSubject<SMSTemplateMaster | null>(
    {} as SMSTemplateMaster,
  );
  whatsappTemplateMasters$ = new BehaviorSubject<WhatsAppTemplateMaster[]>(
    [] as WhatsAppTemplateMaster[],
  );
  whatsappTemplateMaster$ = new BehaviorSubject<WhatsAppTemplateMaster | null>(
    {} as WhatsAppTemplateMaster,
  );
  emailFrom: EmailFromMaster[] = [];
  smsFrom: SMSFromMaster[] = [];
  whatsappFrom: WhatsAppSenderConfigurationMasterResponse[] = [];
  templateList = new BehaviorSubject<any[]>([] as any[]);
  bodyreadOnly: boolean = false;
  btnLoading: boolean = false;
  currentStaff: any;
  chooseTemplates: boolean = false;
  filteredTemplateList: any[] = [];
  searchQuery: string = '';
  plainText: string = '';

  CorporateConfigDetails = CORPORATE_CONFIG_DETAILS;

  // Add a flag to track if templates are already loaded
  private templatesLoaded = {
    email: false,
    sms: false,
    whatsapp: false,
  };

  // Memory leak prevention
  private destroy$ = new Subject<void>();

  constructor() {
    this.messageReplyForm = this.fb.group({
      reply_from: ['', []],
      sender_from: [''],
      subject: [''],
      content: ['', [Validators.required]],
      selected_template: [''],
    });
  }

  ngOnInit(): void {
    this.currentStaff = this.authservice.currentUserDetails();
    if (this.replyFrom().toLowerCase() == 'email') {
      this.showToolbar();
    } else {
      this.hideToolbar();
    }
    this.getNotificationLevelConfigs();
  }

  ngAfterViewInit() {
    this.removeQlImage();
    // Set initial toolbar visibility based on initial reply type
    // setTimeout(() => {
    //   if (this.SelectedReplyFrom.value && this.SelectedReplyFrom.value.name.toLowerCase() !== 'email') {
    //     this.hideToolbar();
    //   }
    //   else{
    //     this.showToolbar();
    //   }
    // }, 500);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    if (
      changes['lastInteraction'] &&
      changes['lastInteraction'].currentValue != undefined &&
      changes['lastInteraction'].previousValue != undefined &&
      changes['lastInteraction'].currentValue !=
        changes['lastInteraction'].previousValue
    ) {
      const currentInteraction = changes['lastInteraction']
        .currentValue as ClientInteraction;
      if (currentInteraction?.interaction_type?.toLowerCase() === 'email') {
        if (
          this.SelectedReplyFrom.value &&
          this.SelectedReplyFrom.value.name.toLowerCase() === 'email'
        ) {
          const isReplyText = currentInteraction.subject
            ?.toLowerCase()
            .includes('re:');
          this.Subject.setValue(
            isReplyText
              ? currentInteraction.subject
              : 'RE: ' + currentInteraction.subject,
          );
          this.showToolbar();
        } else if (
          this.RepliesFrom.value &&
          this.RepliesFrom.value.length > 0
        ) {
          // Configs already loaded but reply form not yet set — update subject via setSelectedReplyFrom
          this.setSelectedReplyFrom();
        }
      }
    }

    if (
      changes['tenantId'] &&
      changes['tenantId'].previousValue &&
      changes['tenantId'].currentValue != changes['tenantId'].previousValue
    ) {
      this.getNotificationLevelConfigs();
    }

    if (
      changes['multiChannel'] &&
      changes['multiChannel'].previousValue != undefined &&
      changes['multiChannel'].currentValue !=
        changes['multiChannel'].previousValue
    ) {
      this.getNotificationLevelConfigs();
    }

    if (
      changes['replyFrom'] &&
      changes['replyFrom'].previousValue != undefined &&
      changes['replyFrom'].currentValue != changes['replyFrom'].previousValue
    ) {
      this.getNotificationLevelConfigs();
    }
  }

  componentInit() {
    this.messageReplyForm = this.fb.group({
      reply_from: ['', []],
      sender_from: [''],
      subject: [''],
      content: ['', [Validators.required]],
      selected_template: [''],
    });
  }

  getNotificationLevelConfigs() {
    if (this.multiChannel()) {
      const ownershipLevel = OwnershipLevel.Tenant;
      const tenantId = this.tenantId();
      const defaultId = 0;
      if (this.notificationMasterStateService.getEmailConfigMasters(tenantId)) {
        this.handleConfigMasters(
          this.notificationMasterStateService.getEmailConfigMasters(tenantId),
          this.notificationMasterStateService.getSmsConfigMasters(tenantId),
          this.notificationMasterStateService.getWhatsappConfigMasters(
            tenantId,
          ),
        );
      } else {
        const emailObservable =
          this.notificationService.getSendEmailFromListByOwnership(
            ownershipLevel,
            tenantId,
            defaultId,
          );
        const smsObservable =
          this.notificationService.getSendSMSFromListByOwnership(
            ownershipLevel,
            tenantId,
            defaultId,
          );
        const whatsappObservable =
          this.notificationService.ListWhatsAppSenderConfigByOwnership(
            ownershipLevel,
            tenantId,
            defaultId,
          );

        forkJoin([emailObservable, smsObservable, whatsappObservable])
          .pipe(
            takeUntil(this.destroy$),
            map(([sendEmailListRes, sendSMSListRes, sendWhatsAppListRes]) => {
              this.notificationMasterStateService.setEmailConfigMasters(
                tenantId,
                sendEmailListRes && sendEmailListRes.items
                  ? sendEmailListRes.items
                  : [],
              );
              this.notificationMasterStateService.setSmsConfigMasters(
                tenantId,
                sendSMSListRes && sendSMSListRes.items
                  ? sendSMSListRes.items
                  : [],
              );
              this.notificationMasterStateService.setWhatsappConfigMasters(
                tenantId,
                sendWhatsAppListRes && sendWhatsAppListRes.items
                  ? sendWhatsAppListRes.items
                  : [],
              );
              this.handleConfigMasters(
                sendEmailListRes && sendEmailListRes.items
                  ? sendEmailListRes.items
                  : [],
                sendSMSListRes && sendSMSListRes.items
                  ? sendSMSListRes.items
                  : [],
                sendWhatsAppListRes && sendWhatsAppListRes.items
                  ? sendWhatsAppListRes.items
                  : [],
              );
            }),
          )
          .subscribe();
      }
    } else {
      const ownershipLevel = OwnershipLevel.Tenant;
      const tenantId = this.tenantId();
      const defaultId = 0;
      if (this.replyFrom().toLowerCase() == 'email') {
        this.notificationService
          .getSendEmailFromListByOwnership(ownershipLevel, tenantId, defaultId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              this.emailFrom = res.items ? res.items : [];
              this.handleConfigMasters(this.emailFrom, [], []);
            },
            error: () => {
              this.emailFrom = [];
              this.handleConfigMasters([], [], []);
            },
          });
      } else if (this.replyFrom().toLowerCase() == 'sms') {
        this.notificationService
          .getSendSMSFromListByOwnership(ownershipLevel, tenantId, defaultId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              this.smsFrom = res.items ? res.items : [];
              this.handleConfigMasters([], this.smsFrom, []);
            },
            error: () => {
              this.smsFrom = [];
              this.handleConfigMasters([], [], []);
            },
          });
      } else if (this.replyFrom().toLowerCase() == 'whatsapp') {
        this.notificationService
          .ListWhatsAppSenderConfigByOwnership(
            ownershipLevel,
            tenantId,
            defaultId,
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              this.whatsappFrom = res.items ? res.items : [];
              this.handleConfigMasters([], [], this.whatsappFrom);
            },
            error: () => {
              this.whatsappFrom = [];
              this.handleConfigMasters([], [], []);
            },
          });
      }
    }
  }

  handleConfigMasters(
    sendEmailListRes: any,
    sendSMSListRes: any,
    sendWhatsAppListRes: any,
  ) {
    this.emailFrom = sendEmailListRes
      ? sendEmailListRes.filter((item: any) => item.status)
      : [];
    this.smsFrom = sendSMSListRes
      ? sendSMSListRes.filter((item: any) => item.status)
      : [];
    this.whatsappFrom = sendWhatsAppListRes
      ? sendWhatsAppListRes.filter(
          (item: any) => item.status.toLowerCase() == 'active',
        )
      : [];

    // Clear existing RepliesFrom array
    const RepliesFrom = [];
    // Build RepliesFrom based on available configurations
    if (this.emailFrom.length > 0) {
      this.emailFrom.forEach((email) => {
        if (this.CorporateConfigDetails['Emails'].includes(email.from_email)) {
          email.isCorporate = true;
        } else {
          email.isCorporate = false;
        }
      });
      RepliesFrom.push(
        ...this.emailFrom.map((email) => ({
          name: 'Email',
          icon: 'fa fa-envelope',
          color: 'bg-orange-500',
          sender: email.from_email,
          senderId: email.id,
          isCorporate: email.isCorporate,
        })),
      );
    }

    if (this.smsFrom.length > 0) {
      this.smsFrom.forEach((sms) => {
        if (this.CorporateConfigDetails['SMS'].includes(sms.from_number)) {
          sms.isCorporate = true;
        } else {
          sms.isCorporate = false;
        }
      });
      RepliesFrom.push(
        ...this.smsFrom.map((sms) => ({
          name: 'Sms',
          icon: 'fa fa-comment',
          color: 'bg-pink-500',
          sender: sms.from_number,
          senderId: sms.id,
          isCorporate: sms.isCorporate,
        })),
      );
    }

    if (this.whatsappFrom.length > 0) {
      this.whatsappFrom.forEach((whatsapp) => {
        if (
          this.CorporateConfigDetails['WhatsApp'].includes(
            whatsapp.display_phonenumber,
          )
        ) {
          whatsapp.isCorporate = true;
        } else {
          whatsapp.isCorporate = false;
        }
      });
      RepliesFrom.push(
        ...this.whatsappFrom.map((whatsapp) => ({
          name: 'WhatsApp',
          icon: 'fab fa-whatsapp',
          color: 'whatsapp-bg',
          sender: whatsapp.display_phonenumber,
          senderId: whatsapp.id,
          isCorporate: whatsapp.isCorporate,
        })),
      );
    }
    this.RepliesFrom.next(RepliesFrom);
    this.cdr.detectChanges();

    // Call setSelectedReplyFrom only after RepliesFrom is populated
    if (this.RepliesFrom.value.length > 0) {
      this.setSelectedReplyFrom();
      this.cdr.detectChanges();
    }
  }

  setSelectedReplyFrom() {
    // Wait for RepliesFrom to be populated
    if (!this.RepliesFrom.value || this.RepliesFrom.value.length === 0) {
      return;
    }
    // Determine interaction type
    const interactionType =
      this.lastInteraction()?.interaction_type?.toLowerCase() || 'email';

    // Find the matching reply option based on interaction type
    let matchingReply = this.RepliesFrom.value.find(
      (x) => x.name.toLowerCase() === interactionType,
    );

    // If no matching reply found, use the first available option
    if (!matchingReply) {
      matchingReply = this.RepliesFrom.value[0];
    }

    // Set up the reply mapping
    const replyFromMap = {
      email: {
        replyFrom: this.RepliesFrom.value.find(
          (x) => x.name.toLowerCase() === 'email',
        ),
        sender: this.emailFrom.filter((o) => o.isCorporate === false)[0],
        validators: [Validators.required],
      },
      sms: {
        replyFrom: this.RepliesFrom.value.find(
          (x) => x.name.toLowerCase() === 'sms',
        ),
        sender: this.smsFrom.filter((o) => o.isCorporate === false)[0],
        validators: [],
      },
      whatsapp: {
        replyFrom: this.RepliesFrom.value.find(
          (x) => x.name.toLowerCase() === 'whatsapp',
        ),
        sender: this.whatsappFrom.filter((o) => o.isCorporate === false)[0],
        validators: [],
      },
    };

    // Get the reply details for the interaction type
    const replyFromDetails = replyFromMap[
      interactionType as keyof typeof replyFromMap
    ] || { replyFrom: matchingReply, sender: null, validators: [] };

    // Set the values if we have a valid reply option
    if (replyFromDetails.replyFrom) {
      this.SelectedReplyFrom.setValue(replyFromDetails.replyFrom);
      this.SelectedSender.setValue(replyFromDetails.sender);
      this.Subject.clearValidators();
      this.Subject.setValidators(replyFromDetails.validators);

      // Handle email subject for thread
      const lastInteraction = this.lastInteraction();
      if (
        replyFromDetails.replyFrom.name.toLowerCase() === 'email' &&
        lastInteraction &&
        lastInteraction.thread_id !== '' &&
        //this.lastInteraction.direction === CommunicationDirection.Incoming &&
        lastInteraction.interaction_type &&
        lastInteraction.interaction_type.toLowerCase() === 'email'
      ) {
        const isReplyText = lastInteraction.subject
          .toLowerCase()
          .includes('re:');
        if (isReplyText) {
          this.Subject.setValue(lastInteraction.subject);
        } else {
          this.Subject.setValue('RE: ' + lastInteraction.subject);
        }
      }

      // if (replyFromDetails.replyFrom.name.toLowerCase() === 'email' &&
      //     this.lastInteraction &&
      //     this.lastInteraction.thread_id !== '' &&
      //     !this.multiChannel &&
      //     this.lastInteraction.interaction_type && this.lastInteraction.interaction_type.toLowerCase() === 'email') {
      //     const isReplyText = this.lastInteraction.subject.toLowerCase().includes('re:');
      //     if(isReplyText)
      //     {
      //       this.Subject.setValue(this.lastInteraction.subject);
      //     }
      //     else{
      //       this.Subject.setValue('RE: ' + this.lastInteraction.subject);
      //     }
      // }
      // Update validations
      this.SelectedReplyFrom.updateValueAndValidity();
      this.SelectedSender.updateValueAndValidity();
      this.Subject.updateValueAndValidity();
      this.messageReplyForm.updateValueAndValidity();

      // Load templates after setting the reply from
      setTimeout(() => {
        this.OnSenderChange({ value: replyFromDetails.replyFrom }, false);
        this.cdr.detectChanges();
      }, 200);
    }
  }

  OnSenderChange(event: any, clearSubject: boolean = true) {
    this.templateList.next([]);
    this.filteredTemplateList = [];
    this.Content.setValue('');
    if (clearSubject) {
      this.Subject.removeValidators([Validators.required]);
      this.Subject.setValue('');
      this.Subject.updateValueAndValidity();
    }
    this.messageReplyForm.updateValueAndValidity();
    if (event.value.name.toLowerCase() === 'email') {
      this.showToolbar();
      this.SelectedSender.setValue(
        this.emailFrom.find((x) => x.id === event.value.senderId),
      );
      this.Subject.setValidators([Validators.required]);
      this.getEmailTemplates();
    } else if (event.value.name.toLowerCase() === 'sms') {
      this.hideToolbar();
      this.SelectedSender.setValue(
        this.smsFrom.find((x) => x.id === event.value.senderId),
      );
      this.Subject.removeValidators([Validators.required]);
      if (this.smsTemplateMasters$.value.length > 0) {
        this.templateList.next(
          this.smsTemplateMasters$.value.filter(
            (item: any) => item.status.toLowerCase() == 'active',
          ),
        );
        this.filteredTemplateList = this.smsTemplateMasters$.value.filter(
          (item: any) => item.status.toLowerCase() == 'active',
        );
      } else {
        this.getSMSTemplates();
      }
    } else if (event.value.name.toLowerCase() === 'whatsapp') {
      this.hideToolbar();
      this.SelectedSender.setValue(
        this.whatsappFrom.find((x) => x.id === event.value.senderId),
      );
      this.Subject.removeValidators([Validators.required]);
      if (this.whatsappTemplateMasters$.value.length > 0) {
        this.templateList.next(
          this.whatsappTemplateMasters$.value.filter(
            (item: any) => item.status.toLowerCase() == 'active',
          ),
        );
        this.filteredTemplateList = this.whatsappTemplateMasters$.value.filter(
          (item: any) => item.status.toLowerCase() == 'active',
        );
      } else {
        this.getWhatsAppTemplates();
      }
    }
    this.messageReplyForm.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  getEmailTemplates() {
    if (this.templatesLoaded.email) {
      this.filteredTemplateList = this.emailTemplateMasters$.value.filter(
        (item: any) =>
          item.status.toLowerCase() == 'active' &&
          (item.event_type == AutomationEventType.Campaign ||
            item.event_type.toString() ==
              AutomationEventType[AutomationEventType.Campaign]),
      );
      this.templateList.next(
        this.emailTemplateMasters$.value.filter(
          (item: any) =>
            item.status.toLowerCase() == 'active' &&
            (item.event_type == AutomationEventType.Campaign ||
              item.event_type.toString() ==
                AutomationEventType[AutomationEventType.Campaign]),
        ),
      );
      this.cdr.markForCheck();
      return;
    } else {
      const tenantId = this.tenantId();
      if (
        this.notificationMasterStateService.getEmailTemplateMasters(tenantId)
      ) {
        const resItems =
          this.notificationMasterStateService.getEmailTemplateMasters(tenantId);
        this.emailTemplateMasters$.next(resItems as EmailTemplateMaster[]);
        this.templateList.next(resItems as any[]);
        this.filteredTemplateList = resItems as any[];
        this.templatesLoaded.email = true;
        this.cdr.markForCheck();
        return;
      } else {
        this.notificationService
          .getEmailTemplatesByOwnership(OwnershipLevel.Tenant, tenantId, 0)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            const resItems = res.items.filter(
              (item: any) =>
                item.status.toLowerCase() == 'active' &&
                (item.event_type == AutomationEventType.Campaign ||
                  item.event_type.toString() ==
                    AutomationEventType[AutomationEventType.Campaign]),
            );
            this.emailTemplateMasters$.next(resItems as EmailTemplateMaster[]);
            this.templateList.next(resItems as any[]);
            this.filteredTemplateList = resItems as any[];
            this.notificationMasterStateService.setEmailTemplateMasters(
              this.tenantId(),
              resItems as EmailTemplateMaster[],
            );
            this.templatesLoaded.email = true;
            this.cdr.markForCheck();
          });
      }
    }
  }

  getSMSTemplates() {
    const tenantId = this.tenantId();
    if (this.notificationMasterStateService.getSmsTemplateMasters(tenantId)) {
      this.smsTemplateMasters$.next(
        this.notificationMasterStateService.getSmsTemplateMasters(tenantId)!,
      );
      this.templateList.next(
        this.notificationMasterStateService.getSmsTemplateMasters(tenantId)!,
      );
      this.filteredTemplateList =
        this.notificationMasterStateService.getSmsTemplateMasters(tenantId)!;
      this.cdr.markForCheck();
    } else {
      this.notificationService
        .getSmsTemplatesByOwnership(OwnershipLevel.Tenant, tenantId, 0)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const resItems = res.items
              ? res.items.filter(
                  (item: any) =>
                    item.status.toLowerCase() == 'active' &&
                    (item.event_type == AutomationEventType.Campaign ||
                      item.event_type.toString() ==
                        AutomationEventType[AutomationEventType.Campaign]),
                )
              : [];
            this.smsTemplateMasters$.next(resItems as SmsTemplateMaster[]);
            this.templateList.next(resItems as any[]);
            this.filteredTemplateList = resItems as any[];
            this.notificationMasterStateService.setSmsTemplateMasters(
              this.tenantId(),
              resItems as SmsTemplateMaster[],
            );
          },
          error: () => {
            this.smsTemplateMasters$.next([]);
            this.templateList.next([]);
            this.filteredTemplateList = [];
          },
        });
    }
  }

  getWhatsAppTemplates() {
    const tenantId = this.tenantId();
    if (
      this.notificationMasterStateService.getWhatsappTemplateMasters(tenantId)
    ) {
      const resItems =
        this.notificationMasterStateService.getWhatsappTemplateMasters(
          tenantId,
        )!;
      this.whatsappTemplateMasters$.next(resItems as WhatsAppTemplateMaster[]);
      const data = resItems.map((item: any) => {
        item.name_primary_language = item.template_name;
        return item;
      });
      this.templateList.next(data as any[]);
      this.filteredTemplateList = data as any[];
      this.cdr.markForCheck();
    } else {
      this.notificationService
        .getWhatsappTemplatesByOwnership(OwnershipLevel.Tenant, tenantId, 0)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const resItems = res.items
              ? res.items.filter(
                  (item: any) =>
                    item.status.toLowerCase() == 'active' &&
                    (item.event_type == AutomationEventType.Campaign ||
                      item.event_type.toString() ==
                        AutomationEventType[AutomationEventType.Campaign]),
                )
              : [];
            this.whatsappTemplateMasters$.next(
              resItems as WhatsAppTemplateMaster[],
            );
            this.notificationMasterStateService.setWhatsappTemplateMasters(
              this.tenantId(),
              resItems as WhatsAppTemplateMaster[],
            );
            const data = resItems.map((item: any) => {
              item.name_primary_language = item.template_name;
              return item;
            });
            this.templateList.next(data as any[]);
            this.filteredTemplateList = data as any[];
          },
          error: () => {
            this.whatsappTemplateMasters$.next([]);
            this.templateList.next([]);
            this.filteredTemplateList = [];
          },
        });
    }
  }

  OnTemplateSelect(event: any) {
    this.chooseTemplates = false;
    this.Subject.setValue('');
    this.Content.setValue('');
    this.SelectedTemplate.setValue(event);
    if (this.SelectedReplyFrom.value.name.toLowerCase() === 'email') {
      if (this.SelectedSender.value.id !== event.from_email_id) {
        this.translateService
          .get('CLIENT_INTERACTION.SENDER_WILL_CHANGE')
          .subscribe((translation) => {
            this.toastMessageService.showInfo(translation);
          });
        this.SelectedSender.setValue(
          this.emailFrom.find((x) => x.id === event.from_email_id),
        );
      }
      this.Subject.setValidators([Validators.required]);
      this.notificationService
        .getEmailTemplateById(this.tenantId(), event.id)
        .subscribe({
          next: (res: EmailTemplateMasterById) => {
            this.emailTemplateMaster$.next(res);

            this.Subject.setValue(
              res.subject_primary_language || res.subject_secondary_language,
            );
            this.Content.setValue(
              res.html_primary_language || res.html_secondary_language,
            );
            if (res.preview_image_path)
              this.insertImage(res.preview_image_path);
            else
              this.insertText(
                res.html_primary_language || res.html_secondary_language,
              );
            this.bodyreadOnly = true;
            this.Subject.disable();
            this.Content.disable();
            this.Content.updateValueAndValidity();
            this.Subject.updateValueAndValidity();
          },
          error: () => {
            this.emailTemplateMaster$.next(null);
            this.Subject.setValue('');
            this.Content.setValue('');
          },
        });
    } else if (this.SelectedReplyFrom.value.name.toLowerCase() === 'sms') {
      this.Subject.removeValidators([Validators.required]);
      if (this.SelectedSender.value.id !== event.sender_id) {
        this.translateService
          .get('CLIENT_INTERACTION.SENDER_WILL_CHANGE')
          .subscribe((translation) => {
            this.toastMessageService.showInfo(translation);
          });
        this.SelectedSender.setValue(
          this.smsFrom.find((x) => x.id === event.sender_id),
        );
      }
      this.notificationService
        .getSMSTemplateById(this.tenantId(), event.id)
        .subscribe({
          next: (res: SMSTemplateMaster) => {
            this.smsTemplateMaster$.next(res);
            this.Content.setValue(
              res.body_primary_language || res.body_secondary_language,
              { onlySelf: true },
            );
            this.insertText(
              res.body_primary_language || res.body_secondary_language,
            );
            this.bodyreadOnly = true;
            this.Content.disable();
            this.Content.updateValueAndValidity();
            this.cdr.markForCheck();
          },
          error: () => {
            this.smsTemplateMaster$.next(null);
            this.Content.setValue('');
          },
        });
    } else if (this.SelectedReplyFrom.value.name.toLowerCase() === 'whatsapp') {
      if (this.SelectedSender.value.id !== event.sender_id) {
        this.translateService
          .get('CLIENT_INTERACTION.SENDER_WILL_CHANGE')
          .subscribe((translation) => {
            this.toastMessageService.showInfo(translation);
          });
        this.SelectedSender.setValue(
          this.whatsappFrom.find((x) => x.id === event.sender_id),
        );
      }
      this.Subject.removeValidators([Validators.required]);
      this.notificationService
        .GetWhatsAppTemplateMasterByOwnership(
          OwnershipLevel.Tenant,
          event.id,
          0,
          this.tenantId(),
        )
        .subscribe({
          next: (res: WhatsAppTemplateMaster) => {
            this.whatsappTemplateMaster$.next(res);
            this.Content.setValue(res.content, { onlySelf: true });
            this.insertText(res.content);
            this.bodyreadOnly = true;
            this.Content.disable();
            this.Content.updateValueAndValidity();
            this.cdr.markForCheck();
          },
          error: () => {
            this.whatsappTemplateMaster$.next(null);
            this.Content.setValue('');
          },
        });
    } else {
      this.Subject.setValue('');
      this.Content.setValue('');
      this.messageReplyForm.updateValueAndValidity();
      this.cdr.markForCheck();
    }
    this.messageReplyForm.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  SendMessage() {
    if (this.messageReplyForm.valid) {
      this.btnLoading = true;
      if (this.SelectedReplyFrom.value.name.toLowerCase() === 'email') {
        this.sendEmail();
      } else if (this.SelectedReplyFrom.value.name.toLowerCase() === 'sms') {
        this.sendSMS();
      } else if (
        this.SelectedReplyFrom.value.name.toLowerCase() === 'whatsapp'
      ) {
        this.sendWhatsApp();
      }
    } else {
      this.messageReplyForm.markAllAsTouched();
    }
  }

  sendEmail() {
    const freeFormPayload: SendFreeFormEmailRequest =
      {} as SendFreeFormEmailRequest;
    const templatePayload: SendEmailTemplateRequest =
      {} as SendEmailTemplateRequest;
    let service: any;
    if (this.SelectedTemplate.value) {
      templatePayload.body = this.Content.value || '';
      templatePayload.email_from_config_id = this.SelectedSender.value.id;
      templatePayload.email_template_master_id = this.SelectedTemplate.value.id;
      templatePayload.subject = this.Subject.value;
      service = this.notificationService.SendTemplatedEmail(
        this.tenantId(),
        [this.client().id],
        templatePayload,
      );
    } else {
      let lastInteractionIncoming: any;
      const lastInteraction = this.lastInteraction();
      if (lastInteraction) {
        lastInteractionIncoming =
          (lastInteraction?.interaction_type.toLowerCase() === 'email' &&
            (lastInteraction.direction === CommunicationDirection.Incoming ||
              lastInteraction.direction.toString() ===
                CommunicationDirection[CommunicationDirection.Incoming])) ||
          lastInteraction.direction === CommunicationDirection.Outgoing ||
          lastInteraction.direction.toString() ===
            CommunicationDirection[CommunicationDirection.Outgoing];
      }
      freeFormPayload.base64 = '';
      freeFormPayload.bcc = [];
      freeFormPayload.body = this.Content.value;
      freeFormPayload.email_from_config_id = this.SelectedSender.value.id;
      freeFormPayload.to_email = [this.client().email_id];
      freeFormPayload.file_extension = '';
      freeFormPayload.file_name = '';
      freeFormPayload.subject = this.Subject.value;
      freeFormPayload.thread_id = lastInteractionIncoming
        ? lastInteraction?.thread_id
        : '';
      freeFormPayload.reply_message_id = lastInteractionIncoming
        ? lastInteraction?.message_id
        : '';
      freeFormPayload.references = lastInteractionIncoming
        ? lastInteraction?.references
          ? lastInteraction?.references + '' + lastInteraction?.message_id
          : lastInteraction?.message_id
        : '';
      service = this.notificationService.SendFreeFormEmail(
        this.tenantId(),
        this.client().id,
        freeFormPayload,
      );
    }
    service.subscribe({
      next: () => {
        this.translateService
          .get('SEND_EMAIL.MSG.EMAIL_SEND')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        this.emitInteraction('Email');
        this.btnLoading = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        if (error.originalError.status == 201) {
          this.translateService
            .get('SEND_EMAIL.MSG.EMAIL_SEND')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.emitInteraction('Email');
          this.cdr.markForCheck();
        } else if (
          error.originalError.error &&
          Array.isArray(error.originalError.error) &&
          error.originalError.error.length > 0
        ) {
          error.originalError.error.forEach((element: NotificationError) => {
            if (element.success) {
              this.translateService
                .get('SEND_EMAIL.MSG.EMAIL_SEND')
                .subscribe((translation) => {
                  this.toastMessageService.showSuccess(translation);
                });
              this.emitInteraction('Email');
            } else
              this.toastMessageService.showErrorMessage(
                element.error.errorMessage,
              );
          });
        } else {
          this.toastMessageService.showError(error);
        }
        this.btnLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  sendSMS() {
    const freeFormPayload: SendFreeFormSmsRequest =
      {} as SendFreeFormSmsRequest;
    const templatePayload: SendSmsTemplateRequest =
      {} as SendSmsTemplateRequest;
    let service: any;
    if (this.SelectedTemplate.value) {
      templatePayload.message = this.Content.value || '';
      templatePayload.sms_sender_config_id = this.SelectedSender.value.id;
      templatePayload.sms_template_master_id = this.SelectedTemplate.value.id;
      service = this.notificationService.SendTemplatedSms(
        this.tenantId(),
        [this.client().id],
        templatePayload,
      );
    } else {
      freeFormPayload.from = this.SelectedSender.value.from_number;
      freeFormPayload.message = this.plainText || '';
      freeFormPayload.sms_sender_config_id = this.SelectedSender.value.id;
      freeFormPayload.to = [
        this.client().mobile_country_code + '' + this.client().mobile_number,
      ];

      service = this.notificationService.SendFreeFormSms(
        this.tenantId(),
        this.client().id,
        freeFormPayload,
      );
    }
    service.subscribe({
      next: () => {
        this.translateService
          .get('SEND_SMS.MSG.SMS_SEND')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        this.emitInteraction('SMS');
        this.btnLoading = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        if (error.originalError.status == 201) {
          this.translateService
            .get('SEND_SMS.MSG.SMS_SEND')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.emitInteraction('SMS');
          this.cdr.markForCheck();
        } else if (
          error.originalError.error &&
          Array.isArray(error.originalError.error) &&
          error.originalError.error.length > 0
        ) {
          error.originalError.error.forEach((element: NotificationError) => {
            if (element.success) {
              this.translateService
                .get('SEND_SMS.MSG.SMS_SEND')
                .subscribe((translation) => {
                  this.toastMessageService.showSuccess(translation);
                });
              this.emitInteraction('SMS');
            } else
              this.toastMessageService.showErrorMessage(
                element.error.errorMessage,
              );
          });
        } else {
          this.toastMessageService.showError(error);
        }
        this.btnLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  sendWhatsApp() {
    let freeFormPayload: any;
    let templatePayload: any;
    let service: any;
    if (this.SelectedTemplate.value) {
      templatePayload = {
        whatsapp_template_master_uuid: this.SelectedTemplate.value.uuid,
        message: this.Content.value,
        whatsapp_sender_config_id: this.SelectedSender.value.id,
      };
      service = this.notificationService.SendTemplatedWhatsAppMessage(
        this.tenantId(),
        [this.client().id],
        templatePayload,
      );
    } else {
      freeFormPayload = {
        to_phone_number:
          this.client().mobile_country_code + this.client().mobile_number,
        content: this.plainText,
        is_marketing_message: true,
      };
      service = this.notificationService.SendWhatsAppMessage(
        this.tenantId(),
        this.client().id,
        freeFormPayload,
      );
    }

    service.subscribe({
      next: () => {
        this.translateService
          .get('SEND_WHATSAPP.MSG.WHATSAPP_SEND')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        this.emitInteraction('Whatsapp');
        this.btnLoading = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        if (
          error.originalError.error &&
          Array.isArray(error.originalError.error) &&
          error.originalError.error.length > 0
        ) {
          error.originalError.error.forEach((element: NotificationError) => {
            if (element.success) {
              this.translateService
                .get('SEND_WHATSAPP.MSG.WHATSAPP_SEND')
                .subscribe((translation) => {
                  this.toastMessageService.showSuccess(translation);
                });
              this.emitInteraction('Whatsapp');
            } else
              this.toastMessageService.showErrorMessage(
                element.error.errorMessage,
              );
          });
        } else {
          this.toastMessageService.showError(error);
          this.btnLoading = false;
          this.cdr.markForCheck();
        }
      },
    });
  }

  emitInteraction(interactionType: string) {
    const newInteraction: ClientInteraction = {} as ClientInteraction;
    newInteraction.interaction_type = interactionType;
    newInteraction.body = this.Content.value;
    newInteraction.subject = this.Subject.value;
    newInteraction.email_notes = '';
    newInteraction.attachments = [];
    newInteraction.sms_content = this.Content.value;
    newInteraction.whatsapp_content = this.Content.value;
    newInteraction.sms_notes = '';
    newInteraction.template_id = this.SelectedTemplate.value
      ? this.SelectedTemplate.value.id
      : '';
    newInteraction.created_by = this.currentStaff.Id;
    newInteraction.created_date = new Date();
    newInteraction.direction = CommunicationDirection.Outgoing;
    newInteraction.is_open = false;
    newInteraction.open_date = '';
    newInteraction.is_click = false;
    newInteraction.click_date = '';

    // Set the exact current date/time without timezone conversions
    // This will help ensure the displayed time matches the actual time the message is sent
    const now = new Date();
    newInteraction.log_date = new Date(now).toISOString().split('.')[0];

    newInteraction.outcome = '';
    newInteraction.showMore = false;
    newInteraction.staff_id = this.currentStaff.Id;
    newInteraction.task_id = 0;
    newInteraction.task_type = '';
    newInteraction.updated_by = 0;
    newInteraction.updated_date = new Date();
    newInteraction.oppt_id = 0;
    newInteraction.notes = '';
    this.newInteraction.emit(newInteraction);
    this.Content.setValue('');
    this.Subject.setValue('');
    this.SelectedTemplate.setValue(null);
    this.cdr.markForCheck();
  }

  getReplyFromInfo(replyFrom: any) {
    if (replyFrom.name.toLowerCase() === 'email') {
      return this.emailFrom[0] ? this.emailFrom[0].from_email : '';
    } else if (replyFrom.name.toLowerCase() === 'sms') {
      return this.smsFrom[0] ? this.smsFrom[0].from_number : '';
    } else if (replyFrom.name.toLowerCase() === 'whatsapp') {
      return this.whatsappFrom[0]
        ? this.whatsappFrom[0].display_phonenumber
        : '';
    } else return '';
  }

  Updatevalue(event: any) {
    this.plainText = this.stripHtml(event);
    this.cdr.markForCheck();
  }

  stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  insertImage(imageUrl: string) {
    const quill = this.editor().quill;
    quill.editor.insertEmbed(0, 'image', imageUrl, 'api');
    this.cdr.markForCheck();
    //quill.editor.insertText(1, 'Hello', 'bold', true,'api');
  }

  insertText(text: string) {
    const quill = this.editor().quill;
    quill.editor.insertText(0, text);
    this.cdr.markForCheck();
    //quill.editor.insertText(1, 'Hello', 'bold', true,'api');
  }

  removeQlImage() {
    const imgButton = document.getElementsByClassName('ql-image');
    for (let i = 0; i < imgButton.length; i++) {
      imgButton[i].remove();
    }

    const cleanButton = document.getElementsByClassName('ql-clean');
    for (let i = 0; i < cleanButton.length; i++) {
      cleanButton[i].remove();
    }

    const codeBlock = document.getElementsByClassName('ql-code-block');
    for (let i = 0; i < codeBlock.length; i++) {
      codeBlock[i].remove();
    }
    const linkButton = document.getElementsByClassName('ql-link');
    for (let i = 0; i < linkButton.length; i++) {
      linkButton[i].remove();
    }
  }

  hideToolbar() {
    setTimeout(() => {
      const toolbar = document.querySelector('.ql-toolbar');
      if (toolbar) {
        toolbar.classList.add('hidden');
      }
      this.cdr.detectChanges();
    }, 100);
  }

  showToolbar() {
    setTimeout(() => {
      const toolbar = document.querySelector('.ql-toolbar');
      if (toolbar) {
        toolbar.classList.remove('hidden');
      }
      this.cdr.detectChanges();
    }, 100);
  }

  get SelectedReplyFrom(): UntypedFormControl {
    return this.messageReplyForm.get('reply_from') as UntypedFormControl;
  }

  get SelectedSender(): UntypedFormControl {
    return this.messageReplyForm.get('sender_from') as UntypedFormControl;
  }

  get Subject(): UntypedFormControl {
    return this.messageReplyForm.get('subject') as UntypedFormControl;
  }

  get Content(): UntypedFormControl {
    return this.messageReplyForm.get('content') as UntypedFormControl;
  }

  get SelectedTemplate(): UntypedFormControl {
    return this.messageReplyForm.get('selected_template') as UntypedFormControl;
  }

  filterTemplates(event: any) {
    const query = event.target.value.toLowerCase();
    this.searchQuery = query;

    if (!query) {
      this.filteredTemplateList = this.templateList.value;
    } else {
      this.filteredTemplateList = this.templateList.value.filter(
        (template: any) =>
          template.name_primary_language.toLowerCase().includes(query),
      );
    }
    this.cdr.markForCheck();
  }

  handleKeyboardShortcut(event: KeyboardEvent) {
    // Check for Ctrl+Enter or Cmd+Enter (Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior
      if (this.messageReplyForm.valid) {
        this.SendMessage();
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions to prevent memory leaks
    this.destroy$.next();
    this.destroy$.complete();
  }
}
